import React, { FC, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import DealerResultsListItem from './components/dealerResultsListItem';
import { iconSize } from '../../../../constants/icon.constants';
import Icon from '../../../@shared/icon';
import { TDealerResultsListingProps } from '../../types/dealerLocatorLanding.types';
import Spinner from '../../../@shared/spinner';
import { BreakpointsInPx } from '../../../../constants/breakpoints.constants';
import useMediaQuery from '../../../../hooks/useMediaQuery';
import './styles.scss';

const DealerResultsListing: FC<TDealerResultsListingProps> = ({
  model,
  dealers,
  dealerIdForPinWithHover,
  clickedDealerNumber,
  isLoadingDealers,
  setDealerIdForPinWithHover,
  setClickedDealerNumber,
  setDealerIdPinHovered,
  country,
  region,
  resultsViewModeInTablet,
}) => {
  const { t } = useTranslation();
  const hasDealers = Array.isArray(dealers) ? dealers.length > 0 : !!dealers.dealership;
  const dealersArray = (() => {
    if (hasDealers && Array.isArray(dealers)) {
      return dealers;
    } else if (hasDealers && !Array.isArray(dealers)) {
      return [dealers];
    }

    return [];
  })();
  const listRef = useRef<HTMLDivElement | null>(null);
  const isWide = useMediaQuery(`(min-width: ${BreakpointsInPx['tbl-l']})`);
  // hiding cards till pin click for map mode in mobile
  const canShowCardsInTablet =
    !isWide && resultsViewModeInTablet === 'map'
      ? !!dealerIdForPinWithHover && dealersArray.find((dealer) => dealer.dealership.dealerNumber === dealerIdForPinWithHover)
      : true;
  const scrollEndTimeOutRef = useRef<ReturnType<typeof setTimeout> | undefined>();
  const [showShadowBottom, setShowShadowBottom] = useState<boolean>(false);
  const [showShadowTop, setShowShadowTop] = useState<boolean>(false);

  const getBoxShadow = (clientHeight: number, scrollTop: number, scrollHeight: number) => {
    const scrolledBy = scrollHeight - Math.round(scrollTop);
    const visibleBottomShadow = scrollHeight > clientHeight && clientHeight < scrolledBy;
    const visibleTopShadow = scrollHeight > clientHeight && scrollHeight > scrolledBy;
    setShowShadowBottom(visibleBottomShadow);
    setShowShadowTop(visibleTopShadow);
  };

  const onScrollHandler = () => {
    if (!listRef?.current) return;
    getBoxShadow(listRef?.current?.clientHeight, listRef?.current?.scrollTop, listRef?.current?.scrollHeight);
  };

  useEffect(() => {
    const resetRefSizes = (ref: any) => {
      if (!ref.current) return;
      getBoxShadow(ref.current.clientHeight as number, ref.current.scrollTop, ref.current.scrollHeight);
    };

    resetRefSizes(listRef);
  }, [listRef?.current?.clientHeight]);

  useEffect(() => {
    if (clickedDealerNumber === null || !listRef?.current) {
      return;
    }

    const listItemIndex = dealersArray.findIndex((dealer) => dealer.dealership.dealerNumber === dealerIdForPinWithHover);
    const listItem = listRef.current?.querySelectorAll('li')[listItemIndex];
    const list = listRef.current;
    const listItemRect = listItem?.getBoundingClientRect();
    const listRect = list?.getBoundingClientRect();

    // auto scroll for mobile screen in list & map mode
    if (!isWide) {
      if (!listItem) return;
      if (resultsViewModeInTablet === 'map') {
        const dealersList = listRef.current?.querySelector('ul');
        if (dealersList) dealersList.scrollLeft = listItem.offsetLeft;
      } else {
        listItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (listItemRect?.bottom > listRect?.bottom) {
      list.scrollTo({
        top: listItem.offsetTop + listItem.clientHeight - list.clientHeight,
        behavior: 'smooth',
      });
    } else if (listItemRect?.top < listRect?.top) {
      list.scrollTo({
        top: listItem.offsetTop,
        behavior: 'smooth',
      });
    }
  }, [clickedDealerNumber, resultsViewModeInTablet, dealers]);

  const highlightVisibleCard = () => {
    const dealersList = listRef.current?.querySelector('ul');
    if (!dealersList) return;
    const visibleCardIndex = Math.round((dealersList.scrollLeft + 8) / (dealersList.clientWidth - 36));

    setDealerIdForPinWithHover(dealersArray[visibleCardIndex].dealership.dealerNumber);
    setClickedDealerNumber(dealersArray[visibleCardIndex].dealership.dealerNumber);
    setDealerIdPinHovered(true);
  };

  const handleUserScroll = () => {
    if (isWide || resultsViewModeInTablet === 'list') return;
    if (scrollEndTimeOutRef.current) clearTimeout(scrollEndTimeOutRef.current);
    scrollEndTimeOutRef.current = setTimeout(highlightVisibleCard, 50);
  };

  return (
    <div
      className={`dealer-locator-landing-dealer-results ${showShadowBottom ? 'dealer-locator-landing-dealer-results--bottom-shadow' : ''}
      ${resultsViewModeInTablet === 'map' ? 'dealer-locator-landing-dealer-results--map-mode' : ''} ${
        showShadowTop ? 'dealer-locator-landing-dealer-results--top-shadow' : ''
      }`}
      ref={listRef}
      onScroll={onScrollHandler}
    >
      {isLoadingDealers && <Spinner extraClassName="dealer-locator-landing-dealer-results__spinner" />}
      {hasDealers ? (
        canShowCardsInTablet && (
          <>
          <div className="dealer-locator-landing-dealer-results__dealers-count">
            {t('cnhi-dealerLocatorResults')}: {dealersArray.length}
          </div>
          <ul className="dealer-locator-landing-dealer-results__list" onScroll={handleUserScroll}>
            {dealersArray.map((dealer, i) => (
              <DealerResultsListItem
                model={model}
                key={dealer.dealership.dealerNumber}
                dealer={dealer}
                clickedDealerNumber={clickedDealerNumber}
                dealerIdForPinWithHover={dealerIdForPinWithHover}
                setClickedDealerNumber={setClickedDealerNumber}
                setDealerIdForPinWithHover={setDealerIdForPinWithHover}
                setDealerIdPinHovered={setDealerIdPinHovered}
                markerNumber={i + 1}
                country={country}
                region={region}
                resultsViewModeInTablet={resultsViewModeInTablet}
              />
            ))}
          </ul>
        </>
        )
      ) 
      : (
        <div className="dealer-locator-landing-dealer-results__no-dealers-msg-box ">
          <Icon
            iconName="action-close-circle"
            extraClassName="dealer-locator-landing-dealer-results__no-dealers-icon"
            size={iconSize['dsk']}
          />
          <div className="dealer-locator-landing-dealer-results__no-dealers-title">{t('cnhi-dealerLocatorNoDealersFound')}</div>
          <p className="dealer-locator-landing-dealer-results__no-dealers-description">
            {t('cnhi-dealerLocatorNoDealersFoundDescription')}
          </p>
        </div>
      )}
    </div>
  );
};

export default DealerResultsListing;
